import { CarouselItemVariants, CarouselVariants } from 'constants/carousel';
import { IMGS_PATH } from 'constants/image';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import CarouselContainer from 'components/Shared/Carousel/CarouselContainer';
import CustomSliderArrow from 'components/ui/CustomSliderArrow';
import Image from 'components/ui/Image';
import tailwind from 'utils/breakpoints';
import { useSnapArrows } from 'hooks/useSnapArrows';
import { useTranslation } from 'hooks/useTranslation';
import baStyles from './BeforeAfterPhotos.module.scss';
import type { APIBeforeAfterPhoto } from 'types/BeforeAfterPhotos';
import type Carousel from 'react-multi-carousel';

type Props = {
  beforeAfterPhotos: APIBeforeAfterPhoto[];
  slug: string;
  totalCount?: string | number;
  type: string;
  showViewPhotos?: boolean;
  limit?: number;
  allPhotos?: boolean;
  loadMorePhotos?: boolean;
};

const BeforeAfterPhotosSnap = ({
  beforeAfterPhotos,
  slug,
  type,
  totalCount = 0,
  showViewPhotos = true,
  limit = 500,
  allPhotos = true,
  loadMorePhotos = true,
}: Props): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<Carousel | null>(null);
  const { locale, t } = useTranslation();
  const { goCarousel } = useSnapArrows('ba-container', 'li');
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [, setIsMobile] = useState(false);
  const [activeSlide] = useState(0);
  const [photos] = useState<APIBeforeAfterPhoto[]>(beforeAfterPhotos || []);
  // useVerticalSwipePreventer(containerRef);

  useEffect(() => {
    setIsMobile(window.matchMedia('(max-width: 1023px)').matches);
  }, []);

  return (
    <div className="w-full" ref={containerRef}>
      <div>
        <div
          className="fluid-lazyload relative"
          // eslint-disable-next-line
          // @ts-ignore
          ref={sliderRef}
        >
          <ul
            id="ba-container"
            className="flex overflow-x-scroll"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {photos.length >= 1 &&
              photos.map((photo, i: number) => {
                if (!photo.beforemedia || !photo.aftermedia) {
                  return null;
                }

                const { aftermedia, beforemedia, beforeafterphoto } = photo;

                return (
                  <li
                    key={`photo_${i}`}
                    onClick={() => {
                      setIsGalleryOpen(true);
                    }}
                    className="shrink-0 w-5/6 sm:w-1/2"
                    style={{ scrollSnapAlign: 'start' }}
                  >
                    <div>
                      <div className="slide border border-silver-light overflow-hidden rounded-lg shrink-0 mx-2 cursor-pointer">
                        <div className="flex">
                          <div className="w-1/2 h-40 lg:h-58 flex items-center justify-center fluid-lazyload relative">
                            {locale === 'de' &&
                            beforeafterphoto?.omit_before_picture ? (
                              <Image
                                src={`${IMGS_PATH.PUBLIC}/before-after-photo-de-placeholder.png`}
                                alt="Placeholder DE - Before/After"
                                className="self-center w-full"
                                fill
                              />
                            ) : (
                              <Image
                                src={beforemedia.url as string}
                                alt={beforemedia.name}
                                className="self-center h-full w-full object-cover"
                                contentful
                                fill
                              />
                            )}
                          </div>
                          <div className="w-1/2 h-40 lg:h-58 flex items-center justify-center fluid-lazyload relative">
                            <Image
                              src={aftermedia.url as string}
                              alt={aftermedia.name}
                              className="self-center h-full w-full object-cover"
                              contentful
                              fill
                            />
                          </div>
                        </div>
                        <div
                          className={clsx(
                            baStyles.label,
                            'text-primary bg-white text-base py-2 pb-3 px-4 border-t relative',
                          )}
                          dangerouslySetInnerHTML={{
                            __html: beforeafterphoto?.description || '',
                          }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
          <div
            className={clsx(
              'hidden md:block w-full absolute',
              baStyles.carouselArrows,
            )}
          >
            <div>
              <CustomSliderArrow
                className="bg-white rounded-full h-8 w-8 p-2 left-0 -ml-0 absolute cursor-pointer hover:bg-bluishGrey"
                direction="left"
                color={tailwind.theme.backgroundColor.navy}
                style={{
                  boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.15)',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
                onClick={() => {
                  goCarousel('previous');
                }}
              />
            </div>
            <div
              className={clsx(
                'right-0 absolute',
                activeSlide === photos.length - 1 && 'hidden',
              )}
            >
              <CustomSliderArrow
                className="bg-white rounded-full h-8 w-8 p-2 right-0 -mr-0 absolute cursor-pointer hover:bg-bluishGrey"
                direction="right"
                color={tailwind.theme.backgroundColor.navy}
                style={{
                  boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.15)',
                  top: '50%',
                  transform: 'translate(50%, -50%)',
                }}
                onClick={() => {
                  goCarousel('next');
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {showViewPhotos && (
        <p className="text-right mt-4">
          <span
            className="underline hidden lg:block cursor-pointer"
            onClick={() => {
              setIsGalleryOpen(true);
            }}
          >
            {t('doctorProfile.viewPhotos')}
          </span>
        </p>
      )}
      {isGalleryOpen && (
        <CarouselContainer
          slides={photos.filter(
            (photo) => photo.aftermedia && photo.beforemedia,
          )}
          onClose={() => {
            setIsGalleryOpen(false);
          }}
          startSlide={0}
          itemVariant={CarouselItemVariants.BEFORE_AFTER}
          variant={CarouselVariants.DEFAULT}
          slug={slug}
          pageType={type}
          totalCount={totalCount}
          limit={limit}
          loadAllPhotos={allPhotos}
          loadMorePhotos={loadMorePhotos}
        />
      )}
    </div>
  );
};

export default BeforeAfterPhotosSnap;
